import React, { useCallback, useMemo, useState, useRef } from 'react';
import { View, StyleSheet, Pressable, Platform, Animated } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useChecklist, runChecklistAction, trackChecklistEvent } from '@/constants/checklist';
import useResponsiveness from '@/hooks/useResponsiveness';

function ChecklistRow({ item, completed, onPress, colors, showCredits }) {
  return (
    <Pressable
      onPress={completed ? undefined : onPress}
      disabled={completed}
      style={({ hovered }) => [
        styles.row,
        hovered && !completed && { backgroundColor: colors.solid_color_tertiary },
      ]}
    >
      <View style={[
        styles.checkbox,
        completed
          ? { backgroundColor: colors.color_success, borderColor: colors.color_success }
          : { borderColor: colors.text_color_tertiary },
      ]}>
        {completed && <Icon name="check" size={8} color="#fff" />}
      </View>

      <View style={[
        styles.iconWrap,
        {
          backgroundColor: completed
            ? 'transparent'
            : `${colors.accent_color_primary}15`,
        },
      ]}>
        <Icon
          name={item.icon}
          size={12}
          color={completed ? colors.text_color_tertiary : colors.text_color_primary}
          solid
        />
      </View>

      <Text
        size="xs"
        weight={completed ? 'regular' : 'medium'}
        numberOfLines={1}
        style={[
          styles.label,
          completed && { textDecorationLine: 'line-through', opacity: 0.4 },
        ]}
      >
        {item.label}
      </Text>

      {showCredits && item.credits > 0 && (
        <View style={[styles.creditChip, {
          backgroundColor: `${colors.color_success}${completed ? '0F' : '1A'}`,
        }]}>
          <Text
            size="xxs"
            weight="bold"
            style={{ color: completed ? `${colors.color_success}73` : colors.color_success }}
          >
            +{item.credits}
          </Text>
        </View>
      )}

      {!completed && (
        <Icon name="chevron-right" size={9} color={colors.text_color_tertiary} />
      )}
    </Pressable>
  );
}

const MemoizedChecklistRow = React.memo(ChecklistRow);

// ─── Daily claim pill (standalone, post-checklist) ───────────

function DailyClaimPill() {
  const { colors } = useTheme();
  const {
    dailyLoading, dailyAvailable, dailyClaiming, dailyJustClaimed, dailyCredits, claimDaily,
  } = useChecklist();

  const pulseAnim = useRef(new Animated.Value(1)).current;

  const handlePress = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_pill_clicked');
    const ok = await claimDaily();
    if (ok) {
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.08, duration: 150, useNativeDriver: false }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 200, useNativeDriver: false }),
      ]).start();
    }
  }, [dailyAvailable, dailyClaiming, claimDaily, pulseAnim]);

  if (!dailyLoading && !dailyAvailable && !dailyJustClaimed) return null;

  const busy = dailyLoading || dailyClaiming;

  return (
    <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
      <Pressable
        onPress={handlePress}
        disabled={busy || dailyJustClaimed}
        style={({ hovered }) => [
          styles.pill,
          dailyJustClaimed
            ? {
                backgroundColor: `${colors.color_success}1A`,
                borderColor: `${colors.color_success}40`,
              }
            : {
                backgroundColor: hovered && !busy
                  ? `${colors.color_success}24`
                  : `${colors.color_success}14`,
                borderColor: `${colors.color_success}33`,
              },
          busy && { opacity: 0.6 },
        ]}
        accessibilityLabel={dailyJustClaimed ? 'Daily credits claimed' : 'Claim daily credits'}
      >
        <View style={styles.pillRingWrap}>
          <Icon
            name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
            size={14}
            color={colors.color_success}
            solid
          />
        </View>
        <Text size="xxs" weight="semibold" style={{ color: dailyJustClaimed ? colors.color_success : colors.text_color_secondary }}>
          {dailyJustClaimed ? 'Claimed!' : 'Daily reward'}
        </Text>
        {!dailyJustClaimed && (
          <View style={[styles.pillBadge, { backgroundColor: `${colors.color_success}1F` }]}>
            <Text size="xxs" weight="bold" style={{ color: colors.color_success }}>
              +{dailyCredits}
            </Text>
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
}

// ─── Checklist pill with popover (pre-completion) ────────────

function ChecklistPill() {
  const { colors } = useTheme();
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const [open, setOpen] = useState(false);
  const {
    completionMap, completedCount, earnableCredits, sortedItems, total,
    dailyAvailable, dailyClaiming, dailyJustClaimed, dailyCredits, claimDaily,
    showCredits,
  } = useChecklist();

  const handleItemPress = useCallback((item) => {
    trackChecklistEvent('item_clicked', item.id);
    setOpen(false);
    if (item.action) {
      runChecklistAction(item.action, { router, openModal, isXS });
      return;
    }
    if (item.route) {
      router.push(item.route);
    }
  }, [router, openModal, isXS]);

  const handleEarnMore = useCallback(() => {
    trackChecklistEvent('earn_more_clicked');
    setOpen(false);
    openModal('SettingsModalWeb', { tab: 'earn' });
  }, [openModal]);

  const handleDailyClaim = useCallback(async () => {
    if (!dailyAvailable || dailyClaiming) return;
    trackChecklistEvent('daily_claim_popover_clicked');
    await claimDaily();
  }, [dailyAvailable, dailyClaiming, claimDaily]);

  const pct = useMemo(() => Math.round((completedCount / total) * 100), [completedCount, total]);
  const circumference = 2 * Math.PI * 8;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const showDailyRow = showCredits && (dailyAvailable || dailyJustClaimed);

  const TriggerComponent = React.memo(() => (
    <Pressable
      style={({ hovered }) => [
        styles.pill,
        {
          backgroundColor: hovered
            ? `${colors.color_success}24`
            : `${colors.color_success}14`,
          borderColor: `${colors.color_success}33`,
        },
      ]}
      accessibilityLabel={`Get started checklist: ${completedCount} of ${total} complete`}
    >
      <View style={styles.pillRingWrap}>
        <svg width={20} height={20} viewBox="0 0 20 20" style={{ position: 'absolute' }}>
          <circle cx={10} cy={10} r={8} fill="none" stroke={colors.border_color_secondary} strokeWidth={2} />
          <circle
            cx={10} cy={10} r={8}
            fill="none"
            stroke={colors.color_success}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 10 10)"
          />
        </svg>
        <Icon name={showCredits ? 'gift' : 'clipboard-check'} size={8} color={colors.color_success} solid />
      </View>
      <Text size="xxs" weight="semibold" color='secondary'>
        {showCredits ? 'Earn credits' : 'Get Started'}
      </Text>
      {showCredits && earnableCredits > 0 && (
        <View style={[styles.pillBadge, { backgroundColor: `${colors.color_success}1F` }]}>
          <Text size="xxs" weight="bold" style={{ color: colors.color_success }}>
            +{earnableCredits}
          </Text>
        </View>
      )}
    </Pressable>
  ));

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <TriggerComponent />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align="end"
        sideOffset={5}
      >
        <View style={styles.popover}>
          {/* Daily claim row */}
          {showDailyRow && (
            <Pressable
              onPress={handleDailyClaim}
              disabled={dailyClaiming || dailyJustClaimed}
              style={({ hovered }) => [
                styles.dailyRow,
                { borderBottomColor: colors.border_color_secondary },
                hovered && !dailyJustClaimed && { backgroundColor: `${colors.color_success}0A` },
                dailyClaiming && { opacity: 0.6 },
              ]}
            >
              <View style={[styles.dailyIconWrap, { backgroundColor: `${colors.color_success}18` }]}>
                <Icon
                  name={dailyJustClaimed ? 'circle-check' : 'calendar-star'}
                  size={12}
                  color={colors.color_success}
                  solid
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text size="xs" weight="semibold" style={{ color: dailyJustClaimed ? colors.color_success : colors.text_color_primary }}>
                  {dailyJustClaimed ? 'Daily credits claimed!' : 'Claim daily credits'}
                </Text>
                <Text size="xxs" color='tertiary'>
                  {dailyJustClaimed ? 'Come back tomorrow' : 'Tap to collect your free credits'}
                </Text>
              </View>
              {!dailyJustClaimed && (
                <View style={[styles.creditChip, { backgroundColor: `${colors.color_success}1A` }]}>
                  <Text size="xxs" weight="bold" style={{ color: colors.color_success }}>
                    +{dailyCredits}
                  </Text>
                </View>
              )}
            </Pressable>
          )}

          {/* Header */}
          <View style={styles.popoverHeader}>
            <View style={styles.popoverHeaderLeft}>
              <View style={[styles.popoverHeaderIcon, { backgroundColor: `${colors.color_success}18` }]}>
                <Icon name="clipboard-check" size={12} color={colors.color_success} solid />
              </View>
              <Text size="s" weight="bold">Get Started</Text>
            </View>
            <Text size="xs" weight="semibold" style={{ color: colors.color_success }}>
              {completedCount}/{total}
            </Text>
          </View>

          {/* Progress bar */}
          <View style={[styles.progressTrack, {
            backgroundColor: colors.solid_color_tertiary,
          }]}>
            <View style={[
              styles.progressFill,
              {
                width: `${pct}%`,
                backgroundColor: colors.color_success,
                ...(Platform.OS === 'web' ? { transition: 'width 0.4s ease' } : {}),
              },
            ]} />
          </View>

          {/* Items */}
          <View style={styles.items}>
            {sortedItems.map((item) => (
              <MemoizedChecklistRow
                key={item.id}
                item={item}
                completed={completionMap[item.id]}
                onPress={() => handleItemPress(item)}
                colors={colors}
                showCredits={showCredits}
              />
            ))}
          </View>

          {showCredits && (
            <View style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}>
              <Pressable
                onPress={handleEarnMore}
                style={({ hovered }) => [
                  styles.footerBtn,
                  hovered && { backgroundColor: `${colors.color_success}0A` },
                ]}
              >
                <Icon name="coins" size={10} color={colors.color_success} />
                <Text size="xxs" weight="semibold" style={{ color: colors.color_success, flex: 1 }}>
                  {earnableCredits > 0 ? `${earnableCredits} credits available` : 'View all rewards'}
                </Text>
                <Icon name="arrow-up-right" size={12} color={colors.color_success} />
              </Pressable>
            </View>
          )}
        </View>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

// ─── Router: picks the right mode ────────────────────────────

function ChecklistProgressPill() {
  const { shouldShow, shouldShowDaily, isComplete } = useChecklist();

  if (shouldShow) return <ChecklistPill />;
  if (shouldShowDaily && isComplete) return <DailyClaimPill />;
  return null;
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 6,
    paddingRight: 10,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
  },
  pillRingWrap: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillBadge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 6,
  },
  popover: {
    width: 300,
    borderRadius: 12,
    overflow: 'hidden',
  },
  popoverHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 8,
  },
  popoverHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  popoverHeaderIcon: {
    width: 24,
    height: 24,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dailyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  dailyIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  progressTrack: {
    height: 3,
    marginHorizontal: 14,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  items: {
    paddingHorizontal: 6,
    paddingBottom: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 7,
    gap: 8,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 5,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconWrap: {
    width: 22,
    height: 22,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  label: {
    flex: 1,
  },
  creditChip: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 4,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
    width: '100%',
    borderTopWidth: 1,
    paddingTop: 7,
  },
  footerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    width: '100%',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
  },
});

export default React.memo(ChecklistProgressPill);
